import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './schedule-date-selector.module.scss';
import {IconButton} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {SVGS} from '../../../../../assets/images';
import PopupMenuActions from './../../../../../components/shared/popup-menu';
import DateLabel from './../../../../../components/shared/nextgen/date-label';
import {PopupCalendar} from './../../../../../components/shared/calendar/index';
import {DateRange, RangeType, Year} from '../../../../shared/calendar/types';
import moment from 'moment/moment';

interface SelectorProps {
    selectedDates: Array<moment.Moment>;
    // eslint-disable-next-line react-redux/no-unused-prop-types
    years: Year[],
    // eslint-disable-next-line react-redux/no-unused-prop-types
    onRangeSelected: (range: moment.Moment[]) => void;
    // eslint-disable-next-line react-redux/no-unused-prop-types
    minDate?: moment.Moment;
    // eslint-disable-next-line react-redux/no-unused-prop-types
    maxDate?: moment.Moment;
    // eslint-disable-next-line react-redux/no-unused-prop-types
    allowedRangeTypes?: RangeType[];
}

interface DaySelectorProps extends SelectorProps {
    isMobile: boolean;
    calendarState?: { open: boolean; setOpen: (value: boolean) => void };
    disabled?: boolean;
}


const DaySelector = (props: DaySelectorProps) => {
    const start = props.selectedDates[0];
    const end = props.selectedDates[1];

    const handleRangeComplete = (range: DateRange) => {
        if (!range[1]) {
            return props.onRangeSelected([range[0]]);
        }

        return props.onRangeSelected([range[0], range[1]]);
    }

    if (!start) {
        return null;
    }

    return(<div className={styles.selector}>
        <DateLabel value={start} type={'day'} />
        {
            end && start.diff(end) !== 0  && <>
                <span className={styles.dash}>-</span>
                <DateLabel value={end} type={'day'} />
            </>
        }
        <PopupCalendar 
            years={props.years} 
            view={'month'} 
            range 
            variant={props.isMobile ? 'sheet' : 'popup'}
            onRangeComplete={handleRangeComplete} 
            value={props.selectedDates as DateRange} 
            minDate={props.minDate} 
            maxDate={props.maxDate}
            allowedRangeTypes={props.allowedRangeTypes}
            state={props.calendarState}
            disabled={props.disabled} />
    </div>)
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const WeekSelector = (props: SelectorProps) => {
    return(<div className={styles.selector}>
        <DateLabel value={props.selectedDates[0]} type={'week'} />
        <PopupMenuActions actionIcon={<SVGS.CalendarIcon />} actions={[]} />
    </div>)
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const MonthSelector = (props: SelectorProps) => {
    return(<div className={styles.selector}>
        <DateLabel value={props.selectedDates[0]} type={'month'} />
        <PopupMenuActions actionIcon={<SVGS.CalendarIcon />} actions={[]} />
    </div>)
}

interface Props {
    selectedDates: Array<moment.Moment>;
    onRangeSelected: (range: moment.Moment[]) => void;
    years: Year[],
    minDate?: moment.Moment;
    maxDate?: moment.Moment;
    allowedRangeTypes?: RangeType[];
    calendarState?: { open: boolean; setOpen: (value: boolean) => void };
    disabled?: boolean;
    compactDesktop?: boolean;
}

const ScheduleDateSelector: React.FC<Props> = (props) => {
    const { selectedDates, years, onRangeSelected, minDate, maxDate, allowedRangeTypes } = props;
    const isMobile = useMediaQuery('(max-width: 1000px)');
    const isDisabled = props.disabled === true;
    const [previousDaysRange, setPreviousDaysRange] = useState(0);
    const startDisabled = isDisabled || (minDate && minDate.diff(selectedDates[0], 'days') >= 0) || false;
    const endDisabled = isDisabled || (maxDate && maxDate.diff(selectedDates[1], 'days') < 1) || false;

    const getWeeksOfMonth = (date: moment.Moment) => {
        const monthStart = date.clone().startOf('month');
        const monthEnd = date.clone().endOf('month');
        const weeks: [moment.Moment, moment.Moment][] = [];
        let cursor = monthStart.clone();
        while (cursor.isSameOrBefore(monthEnd, 'day')) {
            const weekStart = cursor.clone();
            const weekEnd = moment.min(cursor.clone().endOf('week'), monthEnd.clone());
            weeks.push([weekStart, weekEnd]);
            cursor = weekEnd.clone().add(1, 'day');
        }
        return weeks;
    };

    const getWeeksOfYear = (
        date: moment.Moment
    ): [moment.Moment, moment.Moment][] => {
        const yearStart = date.clone().startOf('year');
        const yearEnd   = date.clone().endOf('year');

        const ranges: [moment.Moment, moment.Moment][] = [];
        let cursor = yearStart.clone();

        while (cursor.isSameOrBefore(yearEnd, 'day')) {
            // starting where the previous segment ended + 1 day
            const start = cursor.clone();

            const endOfThisWeek  = start.clone().endOf('week');
            const endOfThisMonth = start.clone().endOf('month');
            const end = moment.min(endOfThisWeek, endOfThisMonth, yearEnd);

            ranges.push([start, end]);

            cursor = end.clone().add(1, 'day');
        }

        return ranges;
    };

    const handleLeftButtonClick = () => {
        const weeks = getWeeksOfYear(selectedDates[0]);
        const currentIndex = weeks.findIndex(
            w => selectedDates[0].isSame(w[0], 'day') && selectedDates[1].isSame(w[1], 'day')
        );

        if (currentIndex > 0) {
            const prevWeek = weeks[currentIndex - 1];
            onRangeSelected(prevWeek);
        } else {
            const start = selectedDates[0];
            const end = selectedDates[1];
            const days = start.diff(end, 'days') - 1;
            if (minDate) {
                const daysToMin = minDate.diff(start, 'days') - 1;
                if (daysToMin === 0) {
                    return;
                }

                if (daysToMin > days) {
                    setPreviousDaysRange(days);
                    onRangeSelected([start.clone().add(daysToMin, 'days'), start.clone().add(-1, 'days')]);
                    return;
                } else if (previousDaysRange > 0) {
                    onRangeSelected([start.clone().add(-previousDaysRange, 'days'), start.clone().add(-1, 'days')]);
                    setPreviousDaysRange(0);
                    return;
                }
            }
            if (days == 0) {
                const newDate = start.add(-1, 'days');
                onRangeSelected([newDate, newDate])
            }
            const range = [start.clone().add(days, 'days'), start.clone().add(-1, 'days')];
            onRangeSelected(range)
        }
    };

    const handleRightButtonClick = () => {
        const weeks = getWeeksOfYear(selectedDates[0]);
        const currentIndex = weeks.findIndex(
            w => selectedDates[0].isSame(w[0], 'day') && selectedDates[1].isSame(w[1], 'day')
        );

        if (currentIndex < weeks.length - 1 && currentIndex > 0) {
            const nextWeek = weeks[currentIndex + 1];
            onRangeSelected(nextWeek);
        } else {
            const start = selectedDates[0];
            const end = selectedDates[1];
            const days = end.diff(start, 'days') + 1
            if (maxDate) {
                const daysToMax = maxDate.diff(end, 'days');
                if (daysToMax < 1) {
                    return
                }

                if (daysToMax < days) {
                    setPreviousDaysRange(days);
                    onRangeSelected([end.clone().add(1, 'day'), end.clone().add(daysToMax, 'days')]);
                    return;
                } else if (previousDaysRange < 0) {
                    onRangeSelected([end.clone().add(1, 'day'), end.clone().add(~previousDaysRange+1, 'days')]);
                    setPreviousDaysRange(0);
                    return;
                }
            }
            if (days < 1) {
                const newDate = end.add(1, 'days');
                onRangeSelected([newDate, newDate])
                return
            }
            const range = [end.clone().add(1, 'day'), end.clone().add(days, 'days')];
            onRangeSelected(range)
        }
    };

    const handleRangeSelected = (range: moment.Moment[]) => {
        if (previousDaysRange !== 0) {
            setPreviousDaysRange(0);
        }

        onRangeSelected(range);
    }

    return(<div className={classNames(styles.container, {
        [styles.compactDesktop]: props.compactDesktop
    })}>
        <IconButton
            className={styles.iconButton}
            onClick={() => handleLeftButtonClick()}
            disabled={startDisabled}
            disableRipple
            disableFocusRipple
            disableTouchRipple
        >
            <SVGS.LeftChevronIcon />
        </IconButton>
        <DaySelector
            selectedDates={selectedDates}
            onRangeSelected={handleRangeSelected}
            years={years}
            minDate={minDate}
            maxDate={maxDate}
            allowedRangeTypes={allowedRangeTypes}
            isMobile={isMobile}
            calendarState={props.calendarState}
            disabled={isDisabled}
        />
        <IconButton
            className={styles.iconButton}
            onClick={() => handleRightButtonClick()}
            disabled={endDisabled}
            disableRipple
            disableFocusRipple
            disableTouchRipple
        >
            <SVGS.RightChevronIcon />
        </IconButton>
    </div>)
}

export default ScheduleDateSelector
